import { useEffect, useRef, useState } from 'react';
import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type HubConnection,
} from '@microsoft/signalr';
import { env } from '@/config/env';
import { tokenStorage } from '@/auth/tokenStorage';

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

export interface SignalRHubOptions {
  // Hub path, e.g. "/hubs/risk-dashboard-live". Combined with the API base.
  url: string;
  // Server -> client event handlers, keyed by method name.
  on?: Record<string, (...args: unknown[]) => void>;
  // Invoked once the connection is (re)established, e.g. to call Subscribe.
  onConnected?: (conn: HubConnection, isReconnect: boolean) => void | Promise<void>;
  // Invoked on teardown while the connection is still Connected, e.g. to call
  // Unsubscribe before stopping. Fire-and-forget; stop() follows immediately.
  onDisconnecting?: (conn: HubConnection) => void;
  enabled?: boolean;
}

// Replacement for the legacy RealtimeManager: token-authenticated SignalR with
// automatic reconnect and a reactive connection status. Tenant scoping is
// handled server-side via the token claim + the hub's own groups.
export function useSignalRHub({ url, on, onConnected, onDisconnecting, enabled = true }: SignalRHubOptions) {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const connectionRef = useRef<HubConnection | null>(null);
  const handlersRef = useRef(on);
  const onConnectedRef = useRef(onConnected);
  const onDisconnectingRef = useRef(onDisconnecting);
  handlersRef.current = on;
  onConnectedRef.current = onConnected;
  onDisconnectingRef.current = onDisconnecting;

  useEffect(() => {
    if (!enabled) return;

    const absolute = `${env.apiBaseUrl}${url}`;
    const connection = new HubConnectionBuilder()
      .withUrl(absolute, { accessTokenFactory: () => tokenStorage.accessToken() ?? '' })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    const handlers = handlersRef.current ?? {};
    for (const [event, handler] of Object.entries(handlers)) {
      connection.on(event, handler);
    }

    connection.onreconnecting(() => setStatus('reconnecting'));
    connection.onreconnected(() => {
      setStatus('connected');
      void onConnectedRef.current?.(connection, true);
    });
    connection.onclose(() => setStatus('disconnected'));

    let cancelled = false;
    setStatus('connecting');
    connection
      .start()
      .then(() => {
        // Effect was torn down (e.g. StrictMode double-mount) while still
        // negotiating — stop cleanly now rather than aborting mid-negotiation.
        if (cancelled) {
          void connection.stop();
          return;
        }
        setStatus('connected');
        void onConnectedRef.current?.(connection, false);
      })
      .catch(() => {
        if (!cancelled) setStatus('disconnected');
      });

    return () => {
      cancelled = true;
      for (const event of Object.keys(handlers)) connection.off(event);
      // Only stop a settled connection; a still-negotiating one is stopped by the
      // start().then() above once negotiation completes (avoids the console error).
      if (connection.state === HubConnectionState.Connected) {
        onDisconnectingRef.current?.(connection);
        void connection.stop();
      }
      connectionRef.current = null;
    };
  }, [url, enabled]);

  return { status, connection: connectionRef };
}
